import { Alert, Box, Divider, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DailyNavReportColumnMapping } from 'api';
import { FileUploadDropzone } from 'components/ui';
import { DailyNavTemplateMapperProps } from './DailyNavTemplateMapper.props';

export const areDailyNavReportMappingsComplete = (
  reportTemplateId: number | null,
  reportColumnMappings: DailyNavReportColumnMapping[]
) =>
  reportTemplateId === null ||
  (reportColumnMappings.length > 0 &&
    reportColumnMappings.every(
      mapping =>
        (mapping.kind === DailyNavReportColumnMapping.kind.FIELD && Boolean(mapping.field)) ||
        mapping.kind === DailyNavReportColumnMapping.kind.LITERAL
    ));

const DailyNavTemplateMapper = ({
  disabled,
  isUploading,
  templateFile,
  reportTemplateId,
  reportTemplateFilename,
  reportColumnMappings,
  reportMappingFields,
  onFileChange,
  onReportColumnMappingsChange,
}: DailyNavTemplateMapperProps) => {
  const mappingsComplete = areDailyNavReportMappingsComplete(reportTemplateId, reportColumnMappings);

  const updateReportMapping = (columnIndex: number, value: string) => {
    onReportColumnMappingsChange(current =>
      current.map(mapping => {
        if (mapping.column_index !== columnIndex) {
          return mapping;
        }
        if (value === 'literal') {
          return {
            column_index: mapping.column_index,
            column_name: mapping.column_name,
            kind: DailyNavReportColumnMapping.kind.LITERAL,
            value: '',
          };
        }
        return {
          column_index: mapping.column_index,
          column_name: mapping.column_name,
          kind: DailyNavReportColumnMapping.kind.FIELD,
          field: value as DailyNavReportColumnMapping.field,
        };
      })
    );
  };

  const updateReportLiteral = (columnIndex: number, value: string) => {
    onReportColumnMappingsChange(current =>
      current.map(mapping => (mapping.column_index === columnIndex ? { ...mapping, value } : mapping))
    );
  };

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h4" gutterBottom>
        Report Template
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Upload the firm&apos;s exact CSV template, then map every column to Daily NAV data or a hardcoded value.
        Column names, duplicates, and order are preserved in the generated report.
      </Typography>
      {reportTemplateId ? (
        <Typography variant="body2" sx={{ mb: 1 }} data-testid="daily-nav-current-template">
          Template: {reportTemplateFilename || `File #${reportTemplateId}`}
        </Typography>
      ) : null}
      <FileUploadDropzone
        accept=".csv,text/csv"
        file={templateFile}
        onFileChange={onFileChange}
        primaryText="Drop a CSV template"
        secondaryText={isUploading ? 'Uploading and reading columns...' : 'or click to browse'}
        testId="daily-nav-report-template-upload"
        disabled={disabled || isUploading}
      />
      {reportColumnMappings.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 2 }} data-testid="daily-nav-report-column-mappings">
          {reportColumnMappings.map(mapping => (
            <Paper
              key={mapping.column_index}
              variant="outlined"
              sx={{ alignItems: 'flex-start', display: 'flex', flexWrap: 'wrap', gap: 2, p: 2 }}>
              <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  CSV column {mapping.column_index + 1}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                  {mapping.column_name || '(blank header)'}
                </Typography>
              </Box>
              <TextField
                select
                size="small"
                label="Maps to"
                value={mapping.kind === DailyNavReportColumnMapping.kind.LITERAL ? 'literal' : (mapping.field ?? '')}
                onChange={event => updateReportMapping(mapping.column_index, event.target.value)}
                disabled={disabled}
                inputProps={{ 'data-testid': `daily-nav-report-mapping-${mapping.column_index}` }}
                sx={{ flex: '1 1 260px' }}>
                <MenuItem value="" disabled>
                  Select a field
                </MenuItem>
                {reportMappingFields.map(field => (
                  <MenuItem key={field.key} value={field.key}>
                    {field.label}
                  </MenuItem>
                ))}
                <MenuItem value="literal">Hardcoded value</MenuItem>
              </TextField>
              {mapping.kind === DailyNavReportColumnMapping.kind.LITERAL && (
                <TextField
                  size="small"
                  label="Hardcoded value"
                  value={mapping.value ?? ''}
                  onChange={event => updateReportLiteral(mapping.column_index, event.target.value)}
                  disabled={disabled}
                  inputProps={{ 'data-testid': `daily-nav-report-literal-${mapping.column_index}` }}
                  sx={{ flex: '1 1 260px' }}
                />
              )}
            </Paper>
          ))}
        </Stack>
      )}
      {reportTemplateId && !mappingsComplete && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Map every CSV column before saving.
        </Alert>
      )}
    </>
  );
};

export default DailyNavTemplateMapper;
